import React from 'react';

import { Dropdown, Flex, type MenuProps } from 'antd-v5';
import {
  MdChevronRight,
  MdDeleteOutline,
  MdEdit,
  MdExpandMore,
  MdMoreVert,
} from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Button } from 'scouting-ui/lib';

import type { SubUnitType } from '@appTypes/esb';
import type { RosterGroup } from '@modules/roster-v2/models/groupedRoster';
import type { SubUnitRef } from '@modules/roster-v2/models/rosterMemberView';
import type { GroupRowContext } from '@shared/components/GroupedTable/useGroupedTable';
import { intl } from '@shared/localization';

import styles from './NewRosterTable.module.scss';

export interface SubUnitActions {
  /** The user may edit and delete it */
  canEdit: (subUnit: SubUnitRef) => boolean;
  onEdit: (subUnitId: number) => void;
  onDelete: (subUnitId: number) => void;
}

export interface GroupHeaderProps {
  row: GroupRowContext<RosterGroup>;
  subUnitType: SubUnitType;
  imageUrl?: string;
  actions?: SubUnitActions;
}

const actionItems: MenuProps['items'] = [
  {
    key: 'edit',
    icon: <MdEdit />,
    label: <FormattedMessage id="shared.edit" />,
  },
  {
    key: 'delete',
    icon: <MdDeleteOutline />,
    danger: true,
    label: <FormattedMessage id="shared.delete" />,
  },
];

const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

interface ActionsMenuProps {
  subUnit: SubUnitRef;
  actions: SubUnitActions;
}

const ActionsMenu: React.FC<ActionsMenuProps> = props => {
  const { subUnit, actions } = props;

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'edit') actions.onEdit(subUnit.id);
    if (key === 'delete') actions.onDelete(subUnit.id);
  };

  return (
    // The menu renders in a portal, but React still bubbles its clicks to the row
    <Flex component="span" flex="none" onClick={stopPropagation}>
      <Dropdown
        trigger={['click']}
        menu={{ items: actionItems, onClick: handleMenuClick }}
      >
        <Button
          type="text"
          color="white"
          variant="text"
          shape="circle"
          size="small"
          icon={<MdMoreVert />}
          aria-label={intl.formatMessage(
            { id: 'rosterV2.subUnits.actions' },
            { label: subUnit.label },
          )}
        />
      </Dropdown>
    </Flex>
  );
};

const GroupHeader: React.FC<GroupHeaderProps> = props => {
  const { row, subUnitType, imageUrl, actions } = props;
  const { group, count, collapsed, continued, toggle } = row;
  const ref = group?.ref;

  // Den labels with a level already read "Wolf Den - 9"
  let label: React.ReactNode = group?.label;
  if (!ref) {
    label = (
      <FormattedMessage id={`rosterV2.table.groups.none.${subUnitType}`} />
    );
  } else if (!ref.denType) {
    label = (
      <FormattedMessage
        id={`rosterV2.table.groups.label.${ref.type}`}
        values={{ name: ref.label }}
      />
    );
  }
  const Chevron = collapsed ? MdChevronRight : MdExpandMore;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggle();
  };

  return (
    <Flex align="center" gap={4}>
      <button
        type="button"
        className={styles.groupToggle}
        aria-expanded={!collapsed}
        title={intl.formatMessage({
          id: collapsed
            ? 'rosterV2.table.groups.expand'
            : 'rosterV2.table.groups.collapse',
        })}
        onClick={handleToggle}
      >
        <Chevron className={styles.groupChevron} aria-hidden />
        {imageUrl && (
          <img src={imageUrl} alt="" className={styles.groupRankImage} />
        )}
        <span className={styles.groupLabel}>{label}</span>
        <span className={styles.groupCount}>({count})</span>
        {continued && (
          <span className={styles.groupContinued}>
            <FormattedMessage id="rosterV2.table.groups.continued" />
          </span>
        )}
      </button>
      {ref && actions?.canEdit(ref) && (
        <ActionsMenu subUnit={ref} actions={actions} />
      )}
    </Flex>
  );
};

export default GroupHeader;
